import { useState, type ReactNode } from 'react';
import { Link } from 'wouter';
import { SideNav } from './SideNav';
import { RunCompleteToast } from './RunCompleteToast';

interface Props {
  children: ReactNode;
  projectId?: string;
}

/**
 * AppShell — desktop: sidebar + main, side-by-side.
 *              mobile (< md): top bar with hamburger + brand mark; sidebar
 *              becomes a slide-in drawer over the content. The drawer
 *              closes whenever a nav link is clicked or the backdrop tapped.
 */
export function AppShell({ children, projectId }: Props) {
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="flex flex-col md:flex-row h-screen overflow-hidden bg-background-dark text-text-primary">
      {/* Mobile top bar — md and up uses the sidebar's own header */}
      <header className="md:hidden flex items-center justify-between gap-3 px-4 py-3 bg-surface border-b border-border-color shrink-0">
        <Link href="/" className="flex items-center gap-2 hover:opacity-90" title="All brands">
          <img src="/bubbles-logo.png" alt="Bubbles" className="h-7 w-7 object-contain" />
          <span className="bubbles-gradient font-display text-lg font-bold tracking-wider uppercase">Bubbles</span>
        </Link>
        <button
          type="button"
          onClick={() => setNavOpen(true)}
          aria-label="Open menu"
          className="flex items-center justify-center w-10 h-10 rounded border border-border-color text-text-primary hover:bg-surface-2 transition-colors"
        >
          <span className="material-symbols-outlined text-[22px]">menu</span>
        </button>
      </header>

      {/* Backdrop for mobile drawer */}
      {navOpen && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setNavOpen(false)}
          className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
        />
      )}

      {/* SideNav: hidden by default on mobile, slides in from the left
          when open. Visible always at md+. */}
      <SideNav projectId={projectId} mobileOpen={navOpen} onMobileClose={() => setNavOpen(false)} />

      <main className="flex-1 overflow-y-auto scroll-thin">{children}</main>
      {projectId && <RunCompleteToast projectId={projectId} />}
    </div>
  );
}
